'use client'

import { Download24, QrCode24, X24 } from '@vinted/monochrome-icons'
import {
  Bubble,
  Button,
  Cell,
  ColorCode,
  Dialog,
  Divider,
  Icon,
  Image,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { Fragment, MouseEvent, useEffect } from 'react'
import { FormattedDate } from 'react-intl'

import {
  useConversationContext,
  useDropOffPointMapContext,
} from '@marketplace-web/domain/core-conversation'
import { UnknownErrorModal } from '@marketplace-web/domain/pay-identity-kyc'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ContentLoader, SeparatedList } from '@marketplace-web/shared/ui-helpers'
import { truncateString } from '@marketplace-web/shared/utils'
import {
  downloadBase64File,
  downloadShippingLabel,
  transformDigitalLabelResponse,
  getDigitalLabel,
} from '@marketplace-web/domain/conversation-shipping-data'
import { colorCodeToHex } from '@marketplace-web/shared/web-ui'

import { clickEvent, selfServiceViewScreenEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { getDefaultShippingAddress } from 'data/api/get-default-shipping-address'
import { transformDefaultShippingAddress } from 'data/api/transformers/response/transform-default-shipping-address'

type Props = {
  isOpen: boolean
  onClose: () => void
  onDropOffPointModalOpen: () => void
}

const VISIBLE_THUMBNAILS_LIMIT = 3
const MAX_CHARACTERS = 20
const INTERNAL_LINK_CLASS_NAME = 'msg-status__link react-drop-off-point-map-link'

const DigitalLabelModal = ({ isOpen, onClose, onDropOffPointModalOpen }: Props) => {
  const translate = useTranslate('conversation.digital_label.modal')
  const { track } = useTracking()

  const { conversation } = useConversationContext()
  const { fetchNearbyDropOffPoints } = useDropOffPointMapContext()

  const transactionId = conversation?.transaction?.id

  const {
    fetch: fetchDigitalLabel,
    transformedData: digitalLabel,
    isLoading,
    error,
  } = useFetch(getDigitalLabel, response => transformDigitalLabelResponse(response))

  const {
    fetch: fetchDefaultShippingAddress,
    transformedData: defaultShippingAddress,
    isLoading: isDefaultShippingAddressLoading,
  } = useFetch(getDefaultShippingAddress, transformDefaultShippingAddress)

  useEffect(() => {
    fetchDefaultShippingAddress()
  }, [fetchDefaultShippingAddress])

  useEffect(() => {
    if (!isOpen || !defaultShippingAddress || isDefaultShippingAddressLoading) return

    fetchNearbyDropOffPoints({ address: defaultShippingAddress })
  }, [defaultShippingAddress, isDefaultShippingAddressLoading, isOpen, fetchNearbyDropOffPoints])

  useEffect(() => {
    if (!transactionId || !isOpen) return

    fetchDigitalLabel({ transactionId })
  }, [transactionId, isOpen, fetchDigitalLabel])

  useEffect(() => {
    if (!isOpen || !transactionId) return

    const targetDetails = {
      screen: Screen.DigitalLabel,
      transactionId,
    }

    track(selfServiceViewScreenEvent(targetDetails))
  }, [track, isOpen, transactionId])

  const handleLinkClickTracking = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return

    const url = event.target.href
    const eventTargetDetails = JSON.stringify({ transaction_id: transactionId, url })

    track(
      clickEvent({
        target: ClickableElement.Hyperlink,
        screen: Screen.DigitalLabel,
        targetDetails: eventTargetDetails,
      }),
    )
  }

  const handleLinkClick = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return
    if (!(event.target.className === INTERNAL_LINK_CLASS_NAME)) {
      handleLinkClickTracking(event)

      return
    }

    event.preventDefault()

    fetchNearbyDropOffPoints({ address: defaultShippingAddress })

    onDropOffPointModalOpen()
  }

  function renderQrCode() {
    return (
      <Cell>
        <div className="u-flexbox u-flex-direction-column u-align-items-center">
          <Text
            as="h1"
            type={Text.Type.Heading}
            text={translate('title')}
            alignment={Text.Alignment.Center}
          />
          <div className="digital-label-modal__image">
            <span role="none" onClick={handleLinkClick}>
              <Text
                text={digitalLabel?.headerSubtitle}
                alignment={Text.Alignment.Center}
                html
                testId="digital-label-modal-header-subtitle"
                as="span"
              />
            </span>
            <Spacer size={Spacer.Size.Large} />
            <Bubble styling={Bubble.Styling.Tight}>
              <Image
                src={digitalLabel?.imageUrl}
                scaling={Image.Scaling.Cover}
                testId="digital-label-modal-image"
              />
            </Bubble>
            <Spacer size={Spacer.Size.Large} />
          </div>
          {digitalLabel?.code && (
            <>
              <Text as="span" text={translate('code_title')} alignment={Text.Alignment.Center} />
              <Text
                as="h1"
                type={Text.Type.Heading}
                text={digitalLabel?.code}
                alignment={Text.Alignment.Center}
              />
            </>
          )}
        </div>
      </Cell>
    )
  }

  function renderShippingCarrier() {
    return (
      <Cell
        title={translate('shipping_carrier')}
        body={digitalLabel?.carrier.name}
        suffix={
          <Image
            src={digitalLabel?.carrier.iconUrl}
            size={Image.Size.Medium}
            styling={Image.Styling.Rounded}
          />
        }
      />
    )
  }

  function renderOrderTitle() {
    if (!digitalLabel) return null

    const { items } = digitalLabel

    if (items.length > 1) {
      const count = items.length.toString()

      return <Text as="span" text={translate('order_count', { count })} />
    }

    return <Text as="span" text={items[0]?.name} />
  }

  function renderOrderPhotos() {
    if (!digitalLabel) return null

    return (
      <>
        {digitalLabel.items.length > VISIBLE_THUMBNAILS_LIMIT && (
          <Image
            label={`+${digitalLabel.items.length - VISIBLE_THUMBNAILS_LIMIT}`}
            styling={Image.Styling.Rounded}
            size={Image.Size.Medium}
            scaling={Image.Scaling.Cover}
            color={colorCodeToHex(ColorCode.GreyscaleLevel4)}
          />
        )}
        {digitalLabel.items.map((item, index) => {
          if (index >= VISIBLE_THUMBNAILS_LIMIT) return null

          return (
            <Fragment key={item.name}>
              <Spacer orientation={Spacer.Orientation.Vertical} />
              <Image
                src={item.imageUrl}
                styling={Image.Styling.Rounded}
                size={Image.Size.Medium}
                scaling={Image.Scaling.Cover}
              />
            </Fragment>
          )
        })}
      </>
    )
  }

  function renderOrder() {
    return (
      <Cell title={translate('order')} body={renderOrderTitle()} suffix={renderOrderPhotos()} />
    )
  }

  function renderRecipient() {
    return (
      <Cell
        title={translate('recipient')}
        suffix={
          <Text
            as="span"
            text={
              digitalLabel?.receiverName &&
              truncateString(digitalLabel.receiverName, MAX_CHARACTERS)
            }
          />
        }
      />
    )
  }

  function renderDeliveryEstimate() {
    return (
      <Cell
        title={translate('delivery_estimate')}
        suffix={
          <Text as="span" alignment={Text.Alignment.Right} text={digitalLabel?.deliveryEstimate} />
        }
      />
    )
  }

  function renderQrCodeExpiration() {
    return (
      <Cell
        title={digitalLabel?.labelExpiresTitle}
        suffix={
          <Text
            as="span"
            text={<FormattedDate value={digitalLabel?.expirationDate} month="long" day="numeric" />}
          />
        }
      />
    )
  }

  function trackDownloadQrCodeClick() {
    const targetDetails = { transaction_id: transactionId }

    track(
      clickEvent({
        screen: Screen.DigitalLabel,
        target: ClickableElement.DownloadDigitalLabel,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  async function handleDownloadQrCodeClick() {
    if (!transactionId) return

    trackDownloadQrCodeClick()

    const response = await downloadShippingLabel(transactionId)

    if ('errors' in response) return

    const base64Data = response.file.label
    const fileName = response.file.filename

    downloadBase64File({ base64Data, fileName })
  }

  function renderDownloadQrCode() {
    return (
      <Cell
        clickable
        prefix={<Icon name={QrCode24} color={Icon.Color.GreyscaleLevel2} />}
        title={digitalLabel?.downloadLabelTitle}
        suffix={<Icon name={Download24} color={Icon.Color.GreyscaleLevel1} />}
        onClick={handleDownloadQrCodeClick}
      />
    )
  }

  function renderContent() {
    if (isLoading) return <ContentLoader testId="digital-label-loader" />

    if (error) return <UnknownErrorModal show onSubmit={onClose} />

    return (
      <>
        <Navigation
          body={digitalLabel?.digitalLabelTitle}
          right={
            <Button
              styling={Button.Styling.Flat}
              onClick={onClose}
              iconName={X24}
              testId="close-qr-code-modal"
              inline
            />
          }
        />
        <Divider />
        <div className="modal-contained-content">
          {renderQrCode()}
          <SeparatedList separator={<Divider />}>
            {renderShippingCarrier()}
            {renderOrder()}
            {renderRecipient()}
            {renderDeliveryEstimate()}
            {renderQrCodeExpiration()}
            {renderDownloadQrCode()}
            <Spacer />
          </SeparatedList>
        </div>
      </>
    )
  }

  return (
    <Dialog show={isOpen} testId="digital-label-modal">
      {renderContent()}
    </Dialog>
  )
}

export default DigitalLabelModal
